import React, { useCallback, useEffect } from "react";

import {
  Text,
  StackProps,
  HStack,
  IconButton,
  IconProps,
  TextProps,
  useClipboard,
  IconButtonProps,
  Tooltip,
  TooltipProps,
} from "@chakra-ui/react";

import { CircleCheckmarkIcon, CopyIcon } from "@/components/shared/ReleaseIcons";

interface Props {
  displayText?: string;
  textToCopy: () => string;
  afterCopy?: (copiedText: string) => void;
  iconProps?: Partial<IconProps>;
  textProps?: Partial<TextProps>;
  buttonProps?: Partial<IconButtonProps>;
  tooltipProps?: Partial<TooltipProps>;
  tooltip?: string;
  showCopyClipboard?: boolean;
}

export default function CopyableText({
  displayText,
  textToCopy,
  afterCopy,
  iconProps,
  textProps,
  buttonProps,
  tooltipProps,
  showCopyClipboard = true,
  tooltip,
  ...otherProps
}: Props & StackProps) {
  const [copiedText, setCopiedText] = React.useState<string>();
  const { onCopy: onCopyToClipboard, setValue, hasCopied } = useClipboard("");

  const copyWrapper = useCallback(() => {
    const text = textToCopy();

    setValue(text);
    setCopiedText(text);
  }, [textToCopy, setValue]);

  useEffect(() => {
    if (typeof copiedText === "undefined" || hasCopied) return;

    // We have to use this useEffect craziness because the onCopy function
    // from useClipboard is a memoized function with the old value of copiedText
    onCopyToClipboard();
  }, [textToCopy, copiedText, onCopyToClipboard, afterCopy, hasCopied]);

  useEffect(() => {
    hasCopied && copiedText && afterCopy && afterCopy(copiedText);

    if (hasCopied && copiedText) {
      setCopiedText(undefined);
    }
  }, [hasCopied, afterCopy, copiedText]);

  const iconButton = (
    <IconButton
      aria-label="copy"
      hidden={!showCopyClipboard}
      icon={hasCopied ? <CircleCheckmarkIcon {...iconProps} /> : <CopyIcon {...iconProps} />}
      size="sm"
      variant="lowlight"
      onClick={copyWrapper}
      {...buttonProps}
    />
  );

  return (
    <HStack {...otherProps}>
      {displayText && <Text {...textProps}>{displayText}</Text>}
      {tooltip ? (
        <Tooltip {...tooltipProps} label={tooltip}>
          {iconButton}
        </Tooltip>
      ) : (
        iconButton
      )}
    </HStack>
  );
}
